/*
  Modifications Copyright (c) 2023 Ctrl IQ, Inc.
*/

import React, { useState, useCallback } from 'react';
import { Link } from 'react-router';
import {
	Label, Button,
	Tooltip
} from '@patternfly/react-core';

import { Tr, Td, ExpandableRowContent } from '@patternfly/react-table';
import { useLingui } from '@lingui/react/macro';
import {
  ExclamationTriangleIcon,
  PencilAltIcon,
  ProjectDiagramIcon,
  RocketIcon,
} from '@patternfly/react-icons';
import { timeOfDay, formatDateString } from 'util/dates';
import { JobTemplatesAPI, WorkflowJobTemplatesAPI } from 'api';
import { ActionsTd, ActionItem, TdBreakWord } from '../PaginatedTable';
import { DetailList, Detail, DeletedDetail } from '../DetailList';
import ChipGroup from '../ChipGroup';
import CredentialChip from '../CredentialChip';
import ExecutionEnvironmentDetail from '../ExecutionEnvironmentDetail';
import { LaunchButton } from '../LaunchButton';
import Sparkline from '../Sparkline';
import CopyButton from '../CopyButton';

function TemplateListItem({
  isExpanded,
  onExpand,
  template,
  isSelected,
  onSelect,
  onCopy,
  detailUrl,
  fetchTemplates,
  rowIndex,
}) {
  const { t } = useLingui();
  const [isDisabled, setIsDisabled] = useState(false);
  const labelId = `check-action-${template.id}`;

  const copyTemplate = useCallback(async () => {
    let response;
    if (template.type === 'job_template') {
      response = await JobTemplatesAPI.copy(template.id, {
        name: `${template.name} @ ${timeOfDay()}`,
      });
    } else {
      response = await WorkflowJobTemplatesAPI.copy(template.id, {
        name: `${template.name} @ ${timeOfDay()}`,
      });
    }
    if (response.status === 201) {
      onCopy(response.data.id);
    }
    await fetchTemplates();
  }, [fetchTemplates, template.id, template.name, template.type, onCopy]);

  const handleCopyStart = useCallback(() => {
    setIsDisabled(true);
  }, []);

  const handleCopyFinish = useCallback(() => {
    setIsDisabled(false);
  }, []);

  const {
    summary_fields: summaryFields,
    ask_inventory_on_launch: askInventoryOnLaunch,
  } = template;

  const missingResourceIcon =
    template.type === 'job_template' &&
    (!summaryFields.project ||
      (!summaryFields.inventory && !askInventoryOnLaunch));

  const inventoryValue = (kind, id) => {
    const inventorykind = kind === 'smart' ? 'smart_inventory' : 'inventory';

    return askInventoryOnLaunch ? (
      <>
        <Link to={`/inventories/${inventorykind}/${id}/details`}>
          {summaryFields.inventory.name}
        </Link>
        <span> {t`(Prompt on launch)`}</span>
      </>
    ) : (
      <Link to={`/inventories/${inventorykind}/${id}/details`}>
        {summaryFields.inventory.name}
      </Link>
    );
  };
  let lastRun = '';
  const mostRecentJob = template.summary_fields.recent_jobs
    ? template.summary_fields.recent_jobs[0]
    : null;
  if (mostRecentJob) {
    lastRun = mostRecentJob.finished
      ? formatDateString(mostRecentJob.finished)
      : t`Running`;
  }

  return (
    <>
      <Tr
        id={`template-row-${template.id}`}
        ouiaId={`template-row-${template.id}`}
      >
        <Td
          expand={{
            rowIndex,
            isExpanded,
            onToggle: onExpand,
          }}
        />
        <Td
          select={{
            rowIndex,
            isSelected,
            onSelect,
          }}
          dataLabel={t`Selected`}
        />
        <TdBreakWord id={labelId} dataLabel={t`Name`}>
          <Link to={`${detailUrl}`}>
            <b>{template.name}</b>
          </Link>
          {missingResourceIcon && (
            <span>
              <Tooltip
                content={t`Resources are missing from this template.`}
                position="right"
              >
                <ExclamationTriangleIcon style={{ color: 'var(--pf-t--global--color--status--danger--default)' }} />
              </Tooltip>
            </span>
          )}
        </TdBreakWord>
        <Td dataLabel={t`Activity`}>
          {summaryFields.recent_jobs ? (
            <Sparkline jobs={summaryFields.recent_jobs} />
          ) : null}
        </Td>
        <Td dataLabel={t`Last Ran`}>{lastRun}</Td>
        <Td dataLabel={t`Type`}>
          {template.type === 'workflow_job_template'
            ? t`Workflow Job Template`
            : t`Job Template`}
        </Td>
        <ActionsTd dataLabel={t`Actions`}>
          <ActionItem
            visible={template.type === 'workflow_job_template'}
            tooltip={t`Visualizer`}
          >
            <Button icon={<ProjectDiagramIcon />}
              ouiaId={`${template.id}-visualizer-button`}
              id={`template-action-visualizer-${template.id}`}
              isDisabled={isDisabled}
              aria-label={t`Visualizer`}
              variant="plain"
              component={Link}
              to={`/templates/workflow_job_template/${template.id}/visualizer`}
             />
          </ActionItem>
          <ActionItem
            visible={template.summary_fields.user_capabilities.start}
            tooltip={t`Launch Template`}
          >
            <LaunchButton resource={template}>
              {({ handleLaunch, isLaunching }) => (
                <Button icon={<RocketIcon />}
                  ouiaId={`${template.id}-launch-button`}
                  id={`template-action-launch-${template.id}`}
                  isDisabled={isDisabled || isLaunching}
                  aria-label={t`Launch template`}
                  variant="plain"
                  onClick={handleLaunch}
                 />
              )}
            </LaunchButton>
          </ActionItem>
          <ActionItem
            visible={template.summary_fields.user_capabilities.edit}
            tooltip={t`Edit Template`}
          >
            <Button icon={<PencilAltIcon />}
              ouiaId={`${template.id}-edit-button`}
              id={`template-action-edit-${template.id}`}
              isDisabled={isDisabled}
              aria-label={t`Edit Template`}
              variant="plain"
              component={Link}
              to={`/templates/${template.type}/${template.id}/edit`}
             />
          </ActionItem>
          <ActionItem
            tooltip={t`Copy Template`}
            visible={template.summary_fields.user_capabilities.copy}
          >
            <CopyButton
              id={`template-action-copy-${template.id}`}
              errorMessage={t`Failed to copy template.`}
              isDisabled={isDisabled}
              onCopyStart={handleCopyStart}
              onCopyFinish={handleCopyFinish}
              copyItem={copyTemplate}
            />
          </ActionItem>
        </ActionsTd>
      </Tr>
      <Tr
        isExpanded={isExpanded}
        ouiaId={`template-row-${template.id}-expanded`}
      >
        <Td colSpan={2} />
        <Td colSpan={4}>
          <ExpandableRowContent>
            <DetailList>
              <Detail
                label={t`Description`}
                value={template.description}
                dataCy={`template-${template.id}-description`}
              />
              {summaryFields.organization ? (
                <Detail
                  label={t`Organization`}
                  value={
                    <Link
                      to={`/organizations/${summaryFields.organization.id}/details`}
                    >
                      {summaryFields.organization.name}
                    </Link>
                  }
                  dataCy={`template-${template.id}-organization`}
                />
              ) : null}
              {summaryFields.inventory ? (
                <Detail
                  label={t`Inventory`}
                  value={inventoryValue(
                    summaryFields.inventory.kind,
                    summaryFields.inventory.id
                  )}
                  dataCy={`template-${template.id}-inventory`}
                />
              ) : (
                !askInventoryOnLaunch &&
                template.type === 'job_template' && (
                  <DeletedDetail label={t`Inventory`} />
                )
              )}
              {summaryFields.project && (
                <Detail
                  label={t`Project`}
                  value={
                    <Link to={`/projects/${summaryFields.project.id}/details`}>
                      {summaryFields.project.name}
                    </Link>
                  }
                  dataCy={`template-${template.id}-project`}
                />
              )}
              {template.type === 'job_template' && (
                <ExecutionEnvironmentDetail
                  executionEnvironment={summaryFields?.execution_environment}
                />
              )}
              <Detail
                label={t`Last Modified`}
                value={formatDateString(template.modified)}
                dataCy={`template-${template.id}-last-modified`}
              />
              {summaryFields.credentials ? (
                <Detail
                  fullWidth
                  label={t`Credentials`}
                  value={
                    <ChipGroup
                      numChips={5}
                      totalChips={summaryFields.credentials.length}
                      ouiaId={`template-${template.id}-credential-chips`}
                    >
                      {summaryFields.credentials.map((c) => (
                        <CredentialChip
                          key={c.id}
                          credential={c}
                          isReadOnly
                          ouiaId={`credential-${c.id}-chip`}
                        />
                      ))}
                    </ChipGroup>
                  }
                  dataCy={`template-${template.id}-credentials`}
                  isEmpty={summaryFields.credentials.length === 0}
                />
              ) : null}
              {summaryFields.labels && (
                <Detail
                  fullWidth
                  label={t`Labels`}
                  value={
                    <ChipGroup
                      numChips={5}
                      totalChips={summaryFields.labels.results.length}
                      ouiaId={`template-${template.id}-label-chips`}
                    >
                      {summaryFields.labels.results.map((l) => (
                        <Label variant="outline"
                          key={l.id}

                          data-ouia-component-id={`label-${l.id}-chip`}
                        >
                          {l.name}
                        </Label>
                      ))}
                    </ChipGroup>
                  }
                  dataCy={`template-${template.id}-labels`}
                  isEmpty={summaryFields.labels.results.length === 0}
                />
              )}
            </DetailList>
          </ExpandableRowContent>
        </Td>
      </Tr>
    </>
  );
}

export { TemplateListItem as _TemplateListItem };
export default TemplateListItem;
